import React, { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import StellarAsset from "../api/types/stellarAsset";
import { AppContext } from "../AppContext";
import SwapSummary from "./forms/SwapSummary";
import { parse as uuidParse, v4 as uuid } from "uuid";
import { FaChevronLeft } from "react-icons/fa";
import ProposalID from "../api/types/poposalID";
import Swap, { SwapID, SwapInfo, SwapJSON } from "../channel/Swap";
import SwapStatusScreen from "./forms/SwapStatusScreen";
import { TypedJSON } from "typedjson";
import { Hub, Hubs } from "../constants/Constants";
import { formatUnits } from "ethers/lib/utils";
import AssetAmountForm from "./forms/AmountAndAssetForm";
import Address from "../api/types/address";
import EthereumAsset from "../api/types/ethereumAsset";
import {
  deserializeAsset,
  deserializeAssets,
  NervosAsset, serializeAsset,
  serializeAssets
} from "../api/types/nervosAsset";

const SwapStages = ["ASSET_AMOUNT_SELECT", "SWAP_SUMMARY", "SWAP_PERFORM"] as const;

interface SwapScreenProps {
  initialStage?: number;
  initialSwapPair?: [EthereumAsset | StellarAsset | NervosAsset, EthereumAsset | StellarAsset | NervosAsset];
  initialSwapAmount?: [string, string];
  initialSwapDecimals?: [number, number];
  initialReceiverAddr?: string;
  initialSwapRate?: number;
  initialFromHub?: Hub;
  initialToHub?: Hub;
  initialSwapInfo?: SwapInfo;
  initialSwapID?: SwapID;
}

function SwapScreen(props: SwapScreenProps) {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);

  const [swapStage, setSwapStage] = useState<number>(props.initialStage || 0);
  const [swapPair, setSwapPair] = useState(props.initialSwapPair);
  const [fromHub, setFromHub] = useState<Hub | undefined>(props.initialFromHub);
  const [toHub, setToHub] = useState<Hub | undefined>(props.initialToHub);
  const [swapAmount, setSwapAmount] = useState(props.initialSwapAmount);
  const [swapDecimals, setSwapDecimals] = useState(props.initialSwapDecimals || [0, 0]);
  const [swapRate, setSwapRate] = useState<number | undefined>(props.initialSwapRate);
  const [receiverAddr, setReceiverAddr] = useState<string | undefined>(props.initialReceiverAddr);
  const [swapInfo, setSwapInfo] = useState<SwapInfo | undefined>(props.initialSwapInfo);
  const [swapID, setSwapID] = useState<SwapID | undefined>(props.initialSwapID);

  // Load state from localStorage if no initial props
  useEffect(() => {
    if (!props.initialSwapPair) {
      try {
        const storedSwap = localStorage.getItem("currentSwap");
        if (!storedSwap) return;

        const parsed = JSON.parse(storedSwap);
        parsed.swapPair = deserializeAssets(parsed.swapPair);
        if (parsed.swapInfo != undefined) {
          parsed.swapInfo.fromAsset = deserializeAsset(parsed.swapInfo.fromAsset);
          parsed.swapInfo.toAsset = deserializeAsset(parsed.swapInfo.toAsset);
        }
        if (parsed.swapStage === 2 && parsed.swapID) {
          setSwapStage(0);
        } else {
          setSwapStage(parsed.swapStage || 0);
        }
        setSwapPair(parsed.swapPair);
        setFromHub(parsed.fromHub);
        setToHub(parsed.toHub);
        setSwapAmount(parsed.swapAmount);
        setSwapDecimals(parsed.swapDecimals || [0, 0]);
        setSwapRate(parsed.swapRate);
        setReceiverAddr(parsed.receiverAddr);
        setSwapInfo(parsed.swapInfo);
        // swapID is stored as hex string; reconstruct to ProposalID so that
        // toString() returns the correct hex value for state map lookups.
        setSwapID(parsed.swapID ? ProposalID.fromJSON(parsed.swapID) : undefined);
      } catch (err) {
        console.error("Failed to load swap from localStorage:", err);
      }
    }
  }, []);

  // Persist swap state whenever it changes
  useEffect(() => {
    const currentSwap = {
      swapStage,
      swapPair: serializeAssets(swapPair!),
      fromHub,
      toHub,
      swapAmount,
      swapDecimals,
      swapRate,
      receiverAddr,
      swapInfo: swapInfo? {
        fromAsset: serializeAsset(swapInfo.fromAsset),
        toAsset: serializeAsset(swapInfo.toAsset),
        fromAmount: swapInfo.fromAmount,
        toAmount: swapInfo.toAmount,
      }: undefined,
      // Store as hex string so JSON.stringify/parse preserves the value.
      // ProposalID (Uint8Array) does not survive a JSON round-trip as an object.
      swapID: swapID?.toString(),
    };
    localStorage.setItem("currentSwap", JSON.stringify(currentSwap));
  }, [swapStage, swapPair, fromHub, toHub, swapAmount, swapDecimals, swapRate, receiverAddr, swapInfo, swapID]);

  // --- Swap stage navigation ---
  const nextSwapPage = () => setSwapStage((prev) => (prev >= SwapStages.length - 1 ? 0 : prev + 1));
  const previousSwapPage = () => setSwapStage((prev) => (prev > 0 ? prev - 1 : prev));

  // --- Swap submission ---
  const submitSwap = () => {
    if (!swapPair || !swapAmount) return console.error("swapPair and swapAmount must not be undefined");

    const swapIDBytes = new Uint8Array(32);
    swapIDBytes.set(uuidParse(uuid()));

    let peerAddressEth = Address.zero();
    let peerAddressCC = "";

    if (toHub!.backendID) {
      peerAddressEth = Address.fromJSON(toHub!.address);
      if (!fromHub!.backendID) peerAddressCC = fromHub!.address;
    } else {
      peerAddressCC = toHub!.address;
      if (fromHub!.backendID) peerAddressEth = Address.fromJSON(fromHub!.address);
    }

    const swap = new Swap(
      ProposalID.fromJSON(swapIDBytes),
      peerAddressEth,
      peerAddressCC,
      swapPair,
      [BigInt(swapAmount[0]), BigInt(swapAmount[1])],
      receiverAddr
    );

    setSwapID(swap.ID);
    setSwapStage(2); // SWAP_PERFORM
    ctx.client.initiateSwap(swap);
  };

  const saveSwap = (swap: Swap) => {
    const swapJSON = TypedJSON.stringify(
      new SwapJSON(
        swap.ID.toString(),
        swap.assets,
        swap.balances,
        swap.peerAddressEth,
        swap.peerAddressCC,
        Date.now(),
        receiverAddr
      ),
      SwapJSON
    );

    let swapsArr: Array<string> = [];
    try {
      swapsArr = JSON.parse(localStorage.getItem("swaps") || "[]");
      swapsArr = swapsArr.map((v) => JSON.stringify(v));
      swapsArr.push(swapJSON);
    } catch (err) {
      swapsArr = [swapJSON];
    }
    localStorage.setItem("swaps", `[${swapsArr.toString()}]`);
  };

  // --- Form rendering ---
  const getForm = (): JSX.Element => {
    switch (SwapStages[swapStage]) {
      case "ASSET_AMOUNT_SELECT":
        return (
          <AssetAmountForm
            fromAsset={swapPair?.[0]?.assetID()}
            toAsset={swapPair?.[1]?.assetID()}
            ownEthAddress={ctx.client.ethAddress}
            ownSteAddress={ctx.client.steAddress}
            ownNervosAddress={ctx.client.nervosAddress}
            submit={async (
              fromAsset,
              toAsset,
              fromAmount,
              toAmount,
              fromDecimal,
              toDecimal,
              receiverAddress,
              rate
            ) => {
              setSwapPair([fromAsset, toAsset]);
              setSwapAmount([fromAmount, toAmount]);
              setSwapDecimals([fromDecimal, toDecimal]);
              setReceiverAddr(receiverAddress);
              setSwapRate(rate);

              const swapInfo = {
                fromAsset,
                toAsset,
                fromAmount: formatUnits(fromAmount, fromDecimal || 0),
                toAmount: formatUnits(toAmount, toDecimal || 0),
              };
              console.log("Setting swap Info: ", swapInfo, fromAmount, toAmount, swapDecimals);
              setSwapInfo(swapInfo);

              const hubFrom = Hubs.get(fromAsset.chainID);
              let hubTo = Hubs.get(toAsset.chainID);

              // Adjust hubs for special cases
              if (fromAsset.chainID.valueOf() === 2n && toAsset.chainID.valueOf() === 11155111n) {
                hubTo = Hubs.get(fromAsset.chainID.valueOf() + toAsset.chainID.valueOf());
              }
              if (toAsset.chainID.valueOf() === 2n && fromAsset.chainID.valueOf() === 11155111n) {
                setFromHub(Hubs.get(fromAsset.chainID.valueOf() + toAsset.chainID.valueOf()));
              }

              setFromHub(hubFrom);
              setToHub(hubTo);

              nextSwapPage();
            }}
          />
        );

      case "SWAP_SUMMARY":
        if (!swapPair || !swapAmount || !swapRate || !receiverAddr) return <></>;
        return (
          <SwapSummary
            swapInfo={swapInfo!}
            exchangeRate={swapRate}
            receiverAddress={receiverAddr}
            hubName={toHub!.name}
            submit={submitSwap}
            cancel={() => setSwapStage(0)}
          />
        );

      case "SWAP_PERFORM":
        if (!swapID || !swapPair || !swapAmount) return <></>;
        return (
          <SwapStatusScreen
            swapID={swapID}
            swapInfo={swapInfo!}
            onSwapCompleted={saveSwap}
            onClose={() => {
              setSwapPair(undefined);
              setSwapAmount(undefined);
              setSwapID(undefined);
              setSwapStage(0);
            }}
          />
        );

      default:
        return <></>;
    }
  };

  return (
    <Container className="SwapContainer mt-2 w-75">
      <h3>{t("tokenSwap")}</h3>
      <hr className="title-divider" />
      {swapStage > 0 && swapStage < 2 && (
        <div className="back-button" onClick={previousSwapPage}>
          <FaChevronLeft />{" "}
        </div>
      )}
      <div className="d-flex h-100 flex-column">{getForm()}</div>
    </Container>
  );
}

export default SwapScreen;
